<template>
    <ol class="mt-2 grid grid-cols-[minmax(3.25rem,max-content)_minmax(0,1fr)] gap-x-2">
        <li
            v-for="step in itinerarySteps"
            :key="step.id"
            class="col-span-2 grid grid-cols-subgrid items-stretch"
        >
            <TrainDepartureLink
                v-if="step.trainLeg && step.time"
                class="justify-end py-3 text-right text-xs! font-semibold! text-ink!"
                :trainLeg="step.trainLeg"
            />
            <time v-else class="py-3 text-right text-xs font-semibold text-ink">
                {{ step.time === undefined ? "" : formatTime(step.time) }}
            </time>
            <div class="border-l pl-2 py-3 flex gap-2 items-start border-line">
                <div
                    class="flex size-4 items-center justify-center rounded-full bg-paper text-primary"
                >
                    <AppIcon class="size-3" :name="stepIcons[step.kind]" />
                </div>
                <div>
                    <p
                        class="leading-tight text-ink-muted"
                        :class="['change', 'walk'].includes(step.kind) ? 'text-xs' : ''"
                    >
                        {{ step.label }}
                        <strong
                            v-if="step.stationCrs"
                            :style="{color: getStationColour(step.stationCrs)}"
                        >
                            {{ getStationName(step.stationCrs) }}
                        </strong>
                        <span
                            v-if="step.kind === 'train' && step.trainLeg?.platform !== undefined"
                            class="ml-1 whitespace-nowrap text-xs font-semibold"
                            :style="{
                                color: getStationColour(step.trainLeg.origin),
                            }"
                        >
                            p{{ step.trainLeg.platform ?? "_" }}
                        </span>
                        {{ step.suffix }}
                    </p>

                    <AlternativeTrains :legs="step.alternativeTrainLegs" class="mt-1" />
                </div>
            </div>
        </li>
    </ol>
</template>

<script setup lang="ts">
import {computed} from "vue";
import AppIcon from "@/components/AppIcon.vue";
import type {IconName} from "@/components/icons";
import {formatTime} from "@/utilities/time.utility.ts";
import type {TimetabledJourney, TrainLeg} from "../../../../dto/timetabledJourney.dto";
import {getStationColour} from "../../../../stations/stationColours";
import {getStationName} from "../../../../stations/stations";
import AlternativeTrains from "../AlternativeTrains.vue";
import TrainDepartureLink from "../TrainDepartureLink.vue";

const props = defineProps<{
    journey: TimetabledJourney;
}>();

type ItineraryStepKind = "arrival" | "change" | "train" | "walk";

interface ItineraryStep {
    id: string;
    kind: ItineraryStepKind;
    label: string;
    stationCrs?: string;
    suffix?: string;
    time?: number;
    trainLeg?: TrainLeg;
    alternativeTrainLegs?: TrainLeg[];
}

const itinerarySteps = computed<ItineraryStep[]>(() => {
    const journey = props.journey;
    const firstSegment = journey.segments.at(0)!;
    const lastSegment = journey.segments.at(-1)!;
    const steps: ItineraryStep[] = [];

    if (firstSegment.kind === "walk") {
        steps.push({
            id: "walk-to-origin",
            kind: "walk",
            time: firstSegment.start,
            label: "Leave for",
            stationCrs: journey.origin,
            suffix: ` · ${formatDuration(firstSegment.start, firstSegment.end)} walk`,
        });
    }

    steps.push(...getTrainItinerarySteps(journey.trainLegs));

    if (lastSegment.kind === "walk") {
        const destinationName = journey.arrivalLabel ?? "your destination";

        steps.push({
            id: "walk-to-destination",
            kind: "walk",
            label: `Walk to ${destinationName} · ${formatDuration(lastSegment.start, lastSegment.end)} walk`,
        });
        steps.push({
            id: "final-arrival",
            kind: "arrival",
            time: lastSegment.end,
            label: `Arrive at ${destinationName}`,
        });
    }

    return steps;
});

function getTrainItinerarySteps(trainLegs: TrainLeg[]): ItineraryStep[] {
    const steps: ItineraryStep[] = [];

    for (const [index, leg] of trainLegs.entries()) {
        steps.push({
            id: `train-${index}`,
            kind: "train",
            time: leg.departure,
            label: "Train from",
            stationCrs: leg.origin,
            trainLeg: leg,
            alternativeTrainLegs: leg.alternativeTrainLegs,
        });
        steps.push({
            id: `arrival-${index}`,
            kind: "arrival",
            time: leg.arrival,
            label: "Arrive",
            stationCrs: leg.destination,
        });

        const nextLeg = trainLegs[index + 1];

        if (nextLeg) {
            steps.push({
                id: `change-${index}`,
                kind: "change",
                label: `Change trains · ${formatDuration(leg.arrival, nextLeg.departure)}`,
            });
        }
    }

    return steps;
}

function formatDuration(start: number, end: number): string {
    const minutes = end - start;
    return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

const stepIcons = {
    arrival: "map-pin",
    change: "clock",
    train: "train",
    walk: "walk",
} satisfies Record<ItineraryStepKind, IconName>;
</script>
